import React from "react";
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

/**
 * Global, reusable skeleton loader for tables.
 * Renders an MUI table shell (header + placeholder rows) while data loads, so
 * the layout doesn't jump when the real rows arrive.
 *
 * Props:
 *  - columns:    number of columns, OR an array of the table's header objects
 *                ([{ headerName }]) / strings. When an array is passed, the real
 *                header labels are shown above the shimmering rows.
 *  - rows:       number of placeholder rows (default 8).
 *  - height:     TableContainer height (default "62vh" — matches the app tables).
 *  - showHeader: render the header row (default true).
 *  - dense:      tighter row padding (default false).
 */
const TableSkeleton = ({
  columns = 6,
  rows = 8,
  height = "62vh",
  showHeader = true,
  dense = false,
}) => {
  const headers = Array.isArray(columns)
    ? columns.map((c) => (typeof c === "string" ? c : c?.headerName || ""))
    : Array.from({ length: Math.max(1, columns) }, () => "");
  const colCount = Math.max(1, headers.length);
  const cellPy = dense ? 0.75 : 1.25;

  return (
    <TableContainer component={Paper} sx={{ overflowX: "auto", height }}>
      <Table stickyHeader>
        {showHeader && (
          <TableHead>
            <TableRow>
              {headers.map((label, i) => (
                <TableCell key={i} sx={{ fontWeight: "bolder" }}>
                  {label || <Skeleton variant="text" width={70} />}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {Array.from({ length: rows }).map((_, r) => (
            <TableRow key={r}>
              {Array.from({ length: colCount }).map((__, c) => (
                <TableCell key={c} sx={{ py: cellPy }}>
                  {/* Vary widths a little so it reads as text, not blocks. */}
                  <Skeleton
                    variant="text"
                    animation="wave"
                    width={`${55 + ((r + c) % 4) * 12}%`}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableSkeleton;
